import React, { useEffect } from "react";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  HeartPulse,
  MessageCircle,
  Phone,
  ShieldAlert,
  ShieldCheck,
  Thermometer,
  Wind,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { HANTAVIRUS, CONTACT_LINKS } from "@/lib/site-data";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const WHATSAPP_HREF = HANTAVIRUS.cta.href;

function WhatsAppPill({ variant = "clay", className = "", label }) {
  const palette =
    variant === "cream"
      ? "bg-cream text-forest hover:bg-cream/95"
      : variant === "ink"
      ? "bg-ink text-cream hover:bg-ink/90"
      : "bg-clay hover:bg-clayHover text-cream";
  return (
    <a
      href={WHATSAPP_HREF}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="hanta-whatsapp-cta"
      className={`inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-semibold shadow-lg hover:-translate-y-0.5 transition-all ${palette} ${className}`}
    >
      <MessageCircle className="h-5 w-5" />
      {label || HANTAVIRUS.cta.label}
      <ArrowRight className="h-4 w-4" />
    </a>
  );
}

export default function HantavirusPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useDocumentMeta({
    title:
      "Hantavirus signs, symptoms and care — Talk to a Nigerian doctor on WhatsApp | TeleMedCare",
    description:
      "Hantavirus awareness: early signs, how it spreads, prevention and when to seek urgent care. Message a licensed Nigerian doctor on WhatsApp in minutes - 24/7 triage with TeleMedCare.",
    canonical: "https://telemedcare.app/hantavirus",
    ogImage:
      "https://telemedcare.app/hantavirus.png",
  });

  const { hero, stats, about, symptoms, transmission, prevention, redFlags, faqs, cta } =
    HANTAVIRUS;

  return (
    <div className="min-h-screen bg-cream text-ink">
      <Navbar />

      <main className="pt-[72px]">
        {/* Awareness ribbon */}
        <div
          role="status"
          aria-live="polite"
          className="bg-clay text-cream"
          data-testid="hanta-ribbon"
        >
          <div className="container-x flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-2.5 text-[13.5px]">
            <span className="inline-flex items-center gap-2 font-medium">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Hantavirus awareness — see signs? Talk to a doctor before it gets worse.
            </span>
            <a
              href={WHATSAPP_HREF}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 underline-offset-4 hover:underline font-semibold"
            >
              Chat now on WhatsApp
              <ChevronRight className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* HERO */}
        <section className="relative overflow-hidden bg-ink text-cream">
          <div
            aria-hidden="true"
            className="absolute -top-40 -right-32 h-[520px] w-[520px] rounded-full bg-clay/25 blur-3xl"
          />
          <div
            aria-hidden="true"
            className="absolute -bottom-40 -left-32 h-[440px] w-[440px] rounded-full bg-forest/40 blur-3xl"
          />

          <div className="container-x relative py-16 lg:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              <div className="lg:col-span-6">
                <p className="inline-flex items-center gap-2 rounded-full bg-clay/15 px-3.5 py-1.5 text-[12.5px] font-semibold uppercase tracking-[0.18em] text-clay">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  {hero.eyebrow}
                </p>
                <h1 className="mt-5 font-display text-[40px] sm:text-[52px] lg:text-[60px] font-extrabold leading-[1.05] tracking-tight text-balance">
                  {hero.title}
                </h1>
                <p className="mt-6 text-[17px] text-cream/85 max-w-xl leading-relaxed">
                  {hero.body}
                </p>

                <div className="mt-10 flex flex-wrap gap-3">
                  <WhatsAppPill variant="clay" />
                  <a
                    href="#symptoms"
                    data-testid="hanta-learn-more"
                    className="inline-flex items-center gap-2 rounded-full border border-cream/30 text-cream px-6 py-3.5 text-[15px] font-semibold hover:bg-cream/10 transition-colors"
                  >
                    Know the symptoms
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
                <p className="mt-4 text-[13.5px] text-cream/65 max-w-md">
                  {cta.helper}
                </p>
              </div>

              <div className="lg:col-span-6">
                <div className="relative">
                  <div
                    aria-hidden="true"
                    className="absolute -inset-3 -z-10 rounded-[32px] bg-clay/25"
                    style={{ transform: "rotate(1.5deg)" }}
                  />
                  <img
                    src={hero.image}
                    alt={hero.image_alt}
                    className="w-full rounded-3xl object-cover aspect-[4/3] shadow-2xl"
                  />
                  <div className="absolute -bottom-5 left-5 right-5 sm:right-auto sm:max-w-[280px] rounded-2xl bg-cream text-ink p-4 shadow-xl">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-ink/55 font-semibold">
                      Genus
                    </p>
                    <p className="mt-1 font-display text-[16px] font-bold leading-snug">
                      Orthohantavirus
                    </p>
                    <p className="mt-1 text-[12.5px] text-ink/65 leading-snug">
                      Family Hantaviridae · spread mainly by infected rodents.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* QUICK FACTS / STATS */}
        <section className="bg-cream border-b border-ink/10 py-14 lg:py-16">
          <div className="container-x">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
              {stats.map((s) => (
                <div key={s.label} data-testid={`hanta-stat-${s.value}`}>
                  <p className="font-display text-[32px] sm:text-[40px] font-extrabold text-forest leading-none">
                    {s.value}
                  </p>
                  <p className="mt-3 text-[14.5px] text-ink/70 leading-snug max-w-[240px]">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ABOUT */}
        <section className="py-20 lg:py-28">
          <div className="container-x">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
              <div className="lg:col-span-7">
                <p className="text-[12.5px] font-semibold uppercase tracking-[0.18em] text-forest">
                  {about.eyebrow}
                </p>
                <h2 className="mt-3 font-display text-[32px] sm:text-[44px] font-extrabold tracking-tight text-balance">
                  {about.title}
                </h2>
                <p className="mt-6 text-[17px] text-ink/70 leading-relaxed max-w-2xl">
                  {about.body}
                </p>
                <ul className="mt-8 space-y-4">
                  {about.highlights.map((h) => (
                    <li key={h} className="flex gap-3.5">
                      <span className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-forest/10 text-forest">
                        <ShieldCheck className="h-4 w-4" />
                      </span>
                      <p className="text-[15.5px] text-ink/80 leading-relaxed">
                        {h}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>

              <aside className="lg:col-span-5">
                <div className="rounded-3xl bg-forest text-cream p-7 lg:p-9 shadow-xl">
                  <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.18em] text-cream/75 font-semibold">
                    <HeartPulse className="h-4 w-4 text-clay" />
                    24/7 doctor on WhatsApp
                  </div>
                  <p className="mt-3 font-display text-[24px] font-bold leading-snug">
                    Not sure if it's malaria, flu or Hantavirus?
                  </p>
                  <p className="mt-3 text-[15px] text-cream/80 leading-relaxed">
                    Symptoms overlap heavily in the first few days. A short WhatsApp chat is enough for a Nigerian doctor to triage your case and tell you what to do next.
                  </p>
                  <div className="mt-6 flex flex-col gap-3">
                    <WhatsAppPill variant="clay" className="w-full justify-center" />
                    <a
                      href={`tel:${CONTACT_LINKS.whatsapp.label.replace(/\s+/g, "")}`}
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-cream/30 px-6 py-3 text-[14.5px] font-semibold text-cream hover:bg-cream/10 transition-colors"
                    >
                      <Phone className="h-4 w-4" />
                      Call {CONTACT_LINKS.whatsapp.label}
                    </a>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* SYMPTOMS */}
        <section id="symptoms" className="bg-white border-y border-ink/10 py-20 lg:py-28">
          <div className="container-x">
            <div className="max-w-3xl">
              <p className="text-[12.5px] font-semibold uppercase tracking-[0.18em] text-clay">
                Recognise it early
              </p>
              <h2 className="mt-3 font-display text-[32px] sm:text-[44px] font-extrabold tracking-tight text-balance">
                {symptoms.title}
              </h2>
              <p className="mt-5 text-[17px] text-ink/70 leading-relaxed">
                {symptoms.subtitle}
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
              {/* Early phase */}
              <div className="rounded-3xl border border-ink/10 bg-cream/60 p-7 lg:p-9">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-clay/15 text-clay">
                    <Thermometer className="h-5 w-5" />
                  </span>
                  <h3 className="font-display text-[20px] font-bold tracking-tight">
                    {symptoms.early.label}
                  </h3>
                </div>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                  {symptoms.early.items.map((it) => (
                    <li key={it} className="flex gap-2.5 text-[15px] text-ink/80 leading-snug">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-clay" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Severe phase */}
              <div className="relative rounded-3xl bg-ink text-cream p-7 lg:p-9 shadow-xl overflow-hidden">
                <div
                  aria-hidden="true"
                  className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-clay/25 blur-3xl"
                />
                <div className="relative">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-clay text-cream">
                      <Wind className="h-5 w-5" />
                    </span>
                    <h3 className="font-display text-[20px] font-bold tracking-tight">
                      {symptoms.severe.label}
                    </h3>
                  </div>
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                    {symptoms.severe.items.map((it) => (
                      <li
                        key={it}
                        className="flex gap-2.5 text-[15px] text-cream/90 leading-snug"
                      >
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-clay" />
                        {it}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7 flex flex-wrap items-center gap-3 text-[13.5px] text-cream/80">
                    <AlertTriangle className="h-4 w-4 text-clay" />
                    Severe phase = go to a hospital. Message us en route.
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-4">
              <WhatsAppPill label="Speak to a doctor about my symptoms" />
              <p className="text-[14px] text-ink/60 max-w-md">
                You can share photos, voice notes and a short symptom history in the chat - the doctor will take it from there.
              </p>
            </div>
          </div>
        </section>

        {/* TRANSMISSION */}
        <section className="py-20 lg:py-28">
          <div className="container-x">
            <div className="max-w-3xl">
              <p className="text-[12.5px] font-semibold uppercase tracking-[0.18em] text-forest">
                How it spreads
              </p>
              <h2 className="mt-3 font-display text-[32px] sm:text-[44px] font-extrabold tracking-tight text-balance">
                {transmission.title}
              </h2>
              <p className="mt-5 text-[17px] text-ink/70 leading-relaxed">
                {transmission.subtitle}
              </p>
            </div>

            <ul className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
              {transmission.routes.map((r, i) => (
                <li
                  key={r.title}
                  className="rounded-3xl border border-ink/10 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-forest/30 hover:shadow-[0_18px_40px_rgba(14,35,51,0.08)]"
                >
                  <span className="font-display text-[42px] font-extrabold text-forest/15 leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-4 font-display text-[18px] font-bold tracking-tight">
                    {r.title}
                  </p>
                  <p className="mt-2 text-[14.5px] text-ink/65 leading-relaxed">
                    {r.body}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* RED FLAG / WHEN TO SEE A DOCTOR */}
        <section className="relative overflow-hidden bg-clay text-cream">
          <div
            aria-hidden="true"
            className="absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full bg-cream/15 blur-3xl"
          />
          <div
            aria-hidden="true"
            className="absolute -bottom-40 -right-20 h-[420px] w-[420px] rounded-full bg-ink/30 blur-3xl"
          />
          <div className="container-x relative py-20 lg:py-28">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              <div className="lg:col-span-7">
                <p className="inline-flex items-center gap-2 rounded-full bg-ink/20 px-3.5 py-1.5 text-[12.5px] font-semibold uppercase tracking-[0.18em] text-cream">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {redFlags.eyebrow}
                </p>
                <h2 className="mt-4 font-display text-[36px] sm:text-[48px] lg:text-[56px] font-extrabold leading-[1.05] tracking-tight text-balance">
                  {redFlags.title}
                </h2>
                <p className="mt-5 text-[17px] text-cream/90 max-w-xl leading-relaxed">
                  {redFlags.body}
                </p>
              </div>
              <div className="lg:col-span-5">
                <div className="rounded-3xl bg-cream text-ink p-7 lg:p-8 shadow-2xl">
                  <p className="text-[12px] uppercase tracking-[0.18em] text-clay font-semibold">
                    Message us right now if
                  </p>
                  <ul className="mt-4 space-y-3">
                    {redFlags.bullets.map((b) => (
                      <li key={b} className="flex gap-3 text-[15px] leading-snug">
                        <AlertTriangle className="h-5 w-5 shrink-0 text-clay" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href={WHATSAPP_HREF}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hanta-redflag-cta"
                    className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest hover:bg-forestHover text-cream px-6 py-3.5 text-[15px] font-semibold shadow-lg hover:-translate-y-0.5 transition-all"
                  >
                    <MessageCircle className="h-5 w-5" />
                    Start the WhatsApp chat now
                    <ArrowRight className="h-4 w-4" />
                  </a>
                  <p className="mt-3 text-[12.5px] text-ink/55 text-center">
                    {CONTACT_LINKS.whatsapp.label} · 24/7 · Free to start
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PREVENTION */}
        <section className="bg-cream py-20 lg:py-28">
          <div className="container-x">
            <div className="max-w-3xl">
              <p className="text-[12.5px] font-semibold uppercase tracking-[0.18em] text-forest">
                Prevention
              </p>
              <h2 className="mt-3 font-display text-[32px] sm:text-[44px] font-extrabold tracking-tight text-balance">
                {prevention.title}
              </h2>
            </div>
            <ul className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {prevention.items.map((item) => (
                <li
                  key={item.title}
                  className="rounded-2xl border border-ink/10 bg-white p-6 lg:p-8 shadow-sm"
                >
                  <div className="flex gap-4">
                    <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest/10 text-forest">
                      <CheckCircle2 className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-display text-[18px] font-bold text-ink">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-[15px] text-ink/70 leading-relaxed">
                        {item.detail}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQs */}
        <section className="bg-white border-t border-ink/10 py-20 lg:py-28">
          <div className="container-x">
            <div className="max-w-3xl">
              <p className="text-[12.5px] font-semibold uppercase tracking-[0.18em] text-clay">
                Frequently asked
              </p>
              <h2 className="mt-3 font-display text-[32px] sm:text-[44px] font-extrabold tracking-tight text-balance">
                Your questions, answered honestly.
              </h2>
            </div>
            <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
              {faqs.map((f) => (
                <div
                  key={f.q}
                  className="rounded-2xl border border-ink/10 bg-cream/60 p-6 lg:p-7"
                >
                  <h3 className="font-display text-[18px] font-bold tracking-tight">
                    {f.q}
                  </h3>
                  <p className="mt-3 text-[15px] text-ink/70 leading-relaxed">
                    {f.a}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-10 text-[13.5px] text-ink/55 max-w-2xl leading-relaxed">
              This page is for awareness only and is not a substitute for medical advice. If symptoms are severe or worsening, go to your nearest hospital immediately.
            </p>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="relative overflow-hidden bg-forest text-cream">
          <div
            aria-hidden="true"
            className="absolute -top-40 -right-40 h-[460px] w-[460px] rounded-full bg-clay/20 blur-3xl"
          />
          <div
            aria-hidden="true"
            className="absolute -bottom-32 -left-24 h-[360px] w-[360px] rounded-full bg-cream/10 blur-3xl"
          />
          <div className="container-x relative py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <p className="text-[12.5px] font-semibold uppercase tracking-[0.18em] text-clay">
                Better health, one message away
              </p>
              <h2 className="mt-4 font-display text-cream text-[40px] sm:text-[56px] lg:text-[64px] font-extrabold leading-[1.02] tracking-tight text-balance">
                Worried about Hantavirus? Chat a doctor in seconds.
              </h2>
              <p className="mt-6 text-[17px] text-cream/85 max-w-xl leading-relaxed">
                No app to install. No insurance required. Send a message - a licensed Nigerian doctor reviews your case and tells you exactly what to do next.
              </p>
            </div>

            <div className="lg:col-span-5">
              <a
                href={WHATSAPP_HREF}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hanta-final-cta"
                className="group inline-flex w-full items-center justify-between rounded-2xl bg-cream text-ink px-6 py-5 hover:-translate-y-0.5 transition-transform shadow-md"
              >
                <span className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-forest text-cream">
                    <MessageCircle className="h-5 w-5" />
                  </span>
                  <span className="text-left">
                    <span className="block text-[12px] uppercase tracking-[0.18em] text-ink/55">
                      Start on WhatsApp
                    </span>
                    <span className="block font-display text-[18px] font-bold">
                      {CONTACT_LINKS.whatsapp.label}
                    </span>
                  </span>
                </span>
                <ArrowRight className="h-5 w-5 text-ink/60 transition-transform group-hover:translate-x-1" />
              </a>
              <p className="mt-3 text-[12.5px] text-cream/65 text-center">
                Average time from message to a licensed doctor: 11 minutes.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Sticky mobile CTA */}
      <div className="fixed inset-x-0 bottom-3 z-40 px-4 sm:hidden">
        <a
          href={WHATSAPP_HREF}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hanta-sticky-cta"
          className="flex items-center justify-center gap-2 rounded-full bg-clay text-cream px-5 py-3.5 text-[15px] font-semibold shadow-2xl"
        >
          <MessageCircle className="h-5 w-5" />
          Chat a doctor — Hantavirus help
        </a>
      </div>
    </div>
  );
}
